import React from 'react'
import { cn } from '../../utils/cn'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  fullWidth?: boolean
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  className,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg disabled:opacity-50 disabled:cursor-not-allowed'

  const variantStyles = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500 [box-shadow:var(--ui-btn-glow)]',
    secondary: 'bg-dark-bg-tertiary text-dark-text border border-dark-border hover:brightness-125 focus-visible:ring-primary-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
    ghost: 'text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text focus-visible:ring-primary-500',
    outline: 'border border-dark-border text-dark-text hover:bg-dark-bg-tertiary focus-visible:ring-primary-500'
  }

  const sizeStyles = {
    sm: '[@media(pointer:coarse)]:min-h-[44px] [[data-ui=glass]_&]:min-h-[44px] px-3 py-1.5 text-sm',
    md: '[@media(pointer:coarse)]:min-h-[44px] [[data-ui=glass]_&]:min-h-[44px] px-4 py-2 text-sm',
    lg: '[@media(pointer:coarse)]:min-h-[44px] [[data-ui=glass]_&]:min-h-[44px] px-6 py-3 text-base'
  }

  return (
    <button
      className={cn(baseStyles, variantStyles[variant], sizeStyles[size], fullWidth && 'w-full', className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  )
}